/**
 * Cross-Tab Activity Tracker
 * Synchronizes user activity across multiple browser tabs to prevent
 * premature logout when user is active in one tab but not others
 */

const ACTIVITY_KEY = 'last_user_activity';
const ACTIVITY_SYNC_INTERVAL = 5000; // Sync every 5 seconds
const HEARTBEAT_KEY = 'activity_heartbeat';

let activitySyncInterval = null;
let lastActivityTime = Date.now();

/**
 * Update the last activity timestamp in localStorage
 * This will be shared across all tabs
 */
const updateActivityTimestamp = () => {
    const timestamp = Date.now();
    lastActivityTime = timestamp;
    try {
        localStorage.setItem(ACTIVITY_KEY, timestamp.toString());
    } catch (error) {
        console.error('Error updating activity timestamp:', error);
    }
};

/**
 * Get the last activity timestamp from localStorage
 * @returns {number} - Timestamp of last activity across all tabs
 */
export const getLastActivityTime = () => {
    try {
        const storedTime = localStorage.getItem(ACTIVITY_KEY);
        if (storedTime) {
            return parseInt(storedTime, 10);
        }
    } catch (error) {
        console.error('Error reading activity timestamp:', error);
    }
    return Date.now();
};

/**
 * Check if the user has been inactive across all tabs
 * @param {number} timeoutMs - Inactivity timeout in milliseconds
 * @returns {boolean} - True if user has been inactive for longer than timeout
 */
export const isInactive = (timeoutMs) => {
    const lastActivity = getLastActivityTime();
    const currentTime = Date.now();
    return (currentTime - lastActivity) > timeoutMs;
};

/**
 * Broadcast activity to other tabs
 */
const broadcastActivity = () => {
    try {
        // Use storage event to notify other tabs
        const heartbeat = Date.now();
        localStorage.setItem(HEARTBEAT_KEY, heartbeat.toString());
        // Immediately remove to allow repeated events
        localStorage.removeItem(HEARTBEAT_KEY);
    } catch (error) {
        console.error('Error broadcasting activity:', error);
    }
};

/**
 * Start tracking user activity across all tabs
 * @param {Array<string>} events - Array of event names to track (e.g., ['mousemove', 'keydown'])
 * @returns {Function} - Cleanup function to stop tracking
 */
export const startCrossTabActivityTracking = (events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click']) => {
    // Initialize activity timestamp
    updateActivityTimestamp();

    // Handler for user activity events
    const handleActivity = () => {
        updateActivityTimestamp();
        broadcastActivity();
    };

    // Add event listeners for user activity
    events.forEach(event => {
        window.addEventListener(event, handleActivity, { passive: true });
    });

    // Periodic sync to ensure activity state is maintained
    activitySyncInterval = setInterval(() => {
        // Read the latest activity from localStorage
        // This catches activity from other tabs
        const storedActivity = getLastActivityTime();
        // Mirror the authoritative value from localStorage
        lastActivityTime = storedActivity;
    }, ACTIVITY_SYNC_INTERVAL);

    // Listen for heartbeat from other tabs
    const handleStorageChange = (e) => {
        if (e.key === HEARTBEAT_KEY && e.newValue) {
            // Another tab had activity, update our local timestamp
            const timestamp = parseInt(e.newValue, 10);
            if (!isNaN(timestamp) && timestamp > lastActivityTime) {
                lastActivityTime = timestamp;
                // Also update the main activity key
                try {
                    localStorage.setItem(ACTIVITY_KEY, timestamp.toString());
                } catch (error) {
                    console.error('Error syncing activity:', error);
                }
            }
        } else if (e.key === ACTIVITY_KEY && e.newValue) {
            // Direct activity key update from another tab
            const timestamp = parseInt(e.newValue, 10);
            if (!isNaN(timestamp) && timestamp > lastActivityTime) {
                lastActivityTime = timestamp;
            }
        }
    };

    window.addEventListener('storage', handleStorageChange);

    // Cleanup function
    return () => {
        events.forEach(event => {
            window.removeEventListener(event, handleActivity);
        });

        if (activitySyncInterval) {
            clearInterval(activitySyncInterval);
            activitySyncInterval = null;
        }

        window.removeEventListener('storage', handleStorageChange);
    };
};

/**
 * Reset the inactivity timer across all tabs
 * Useful for manual resets (e.g., after successful API calls)
 */
export const resetCrossTabActivity = () => {
    updateActivityTimestamp();
    broadcastActivity();
};

/**
 * Clear activity tracking data
 * Call this when user logs out
 */
export const clearActivityTracking = () => {
    try {
        localStorage.removeItem(ACTIVITY_KEY);
        localStorage.removeItem(HEARTBEAT_KEY);
    } catch (error) {
        console.error('Error clearing activity tracking:', error);
    }
};

/**
 * Get the time remaining until inactivity timeout
 * @param {number} timeoutMs - Inactivity timeout in milliseconds
 * @returns {number} - Milliseconds remaining, or 0 if already timed out
 */
export const getTimeUntilTimeout = (timeoutMs) => {
    const lastActivity = getLastActivityTime();
    const currentTime = Date.now();
    const elapsed = currentTime - lastActivity;
    const remaining = timeoutMs - elapsed;
    return Math.max(0, remaining);
};
